/**
 * 薪酬配置（薪酬阶段1）—— admin 后台，挂 ControlPanel ?tab=salary。
 * 三块：① 班型定死价（合同选班型带价的权威源）② 职级底薪 ③ 提成矩阵[职级×班型]→单笔奖金。
 * 改矩阵 = 升版本（v+1，历史订单按快照锁定不受影响）+ 二次确认（拍板⑤）+ 后端 @Audit 落审计。
 */
import { useEffect, useState } from 'react';
import { Table, InputNumber, Input, Switch, Button, Popconfirm, message, Tag, Space } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';

interface Grade { gradeKey: string; label: string; baseSalary: string; sortOrder: number; isActive: number }
interface ClassType { classTypeKey: string; label: string; standardPrice: string; sortOrder: number; isActive: number }
interface MatrixRow { gradeKey: string; classTypeKey: string; bonusAmount: string; matrixVersion: number }
interface SalaryConfigData { grades: Grade[]; classTypes: ClassType[]; currentVersion: number; matrix: MatrixRow[] }

export function SalaryConfig() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<SalaryConfigData>({
    queryKey: ['salary-config'],
    queryFn: () => $.get('/salary/config'),
    staleTime: 30_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <ClassTypeSection data={data} loading={isLoading} onSaved={() => { qc.invalidateQueries({ queryKey: ['salary-config'] }); qc.invalidateQueries({ queryKey: ['salary-class-types'] }); /* 评审#9：改价/启停同步刷新合同班型下拉缓存 */ }} />
      <GradeSection data={data} loading={isLoading} onSaved={() => qc.invalidateQueries({ queryKey: ['salary-config'] })} />
      <MatrixSection data={data} loading={isLoading} onSaved={() => { qc.invalidateQueries({ queryKey: ['salary-config'] }); }} />
    </div>
  );
}

function Card({ title, extra, children }: { title: string; extra?: any; children: any }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E6EB', borderRadius: 8, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: '#1F2329' }}>{title}</div>
        <div>{extra}</div>
      </div>
      {children}
    </div>
  );
}

// ① 班型定死价
function ClassTypeSection({ data, loading, onSaved }: { data?: SalaryConfigData; loading: boolean; onSaved: () => void }) {
  const [draft, setDraft] = useState<Record<string, { label: string; standardPrice: number; isActive: boolean }>>({});
  useEffect(() => {
    if (!data?.classTypes) return;
    const d: any = {};
    data.classTypes.forEach((c) => { d[c.classTypeKey] = { label: c.label, standardPrice: Number(c.standardPrice), isActive: !!c.isActive }; });
    setDraft(d);
  }, [data?.classTypes]);

  const save = useMutation({
    mutationFn: (key: string) => $.patch(`/salary/class-types/${encodeURIComponent(key)}`, draft[key]),
    onSuccess: () => { message.success('班型已保存'); onSaved(); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '保存失败'),
  });

  const cols = [
    { title: '班型键', dataIndex: 'classTypeKey', width: 100, render: (v: string) => <Tag>{v}</Tag> },
    { title: '显示名', dataIndex: 'classTypeKey', render: (k: string) => (
      <Input value={draft[k]?.label ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: { ...d[k], label: e.target.value } }))} style={{ width: 140 }} />
    ) },
    { title: '定死价 ¥', dataIndex: 'classTypeKey', render: (k: string) => (
      <InputNumber value={draft[k]?.standardPrice} min={1} onChange={(v) => setDraft((d) => ({ ...d, [k]: { ...d[k], standardPrice: Number(v ?? 0) } }))} style={{ width: 120 }} />
    ) },
    { title: '启用', dataIndex: 'classTypeKey', width: 80, render: (k: string) => (
      <Switch checked={draft[k]?.isActive} onChange={(v) => setDraft((d) => ({ ...d, [k]: { ...d[k], isActive: v } }))} />
    ) },
    { title: '操作', dataIndex: 'classTypeKey', width: 90, render: (k: string) => (
      <Button size="small" type="link" loading={save.isPending} onClick={() => save.mutate(k)}>保存</Button>
    ) },
  ];
  return (
    <Card title="① 班型定死价（合同选班型带价的权威源）">
      <div style={{ fontSize: 12, color: '#8F959E', marginBottom: 8 }}>改价只影响之后新建的合同；历史合同/订单金额不变。停用的班型不可再建新合同。</div>
      <Table rowKey="classTypeKey" size="small" loading={loading} pagination={false} dataSource={data?.classTypes ?? []} columns={cols as any} />
    </Card>
  );
}

// ② 职级底薪
function GradeSection({ data, loading, onSaved }: { data?: SalaryConfigData; loading: boolean; onSaved: () => void }) {
  const [draft, setDraft] = useState<Record<string, { label: string; baseSalary: number; isActive: boolean }>>({});
  useEffect(() => {
    if (!data?.grades) return;
    const d: any = {};
    data.grades.forEach((g) => { d[g.gradeKey] = { label: g.label, baseSalary: Number(g.baseSalary), isActive: !!g.isActive }; });
    setDraft(d);
  }, [data?.grades]);

  const save = useMutation({
    mutationFn: (key: string) => $.patch(`/salary/grades/${encodeURIComponent(key)}`, draft[key]),
    onSuccess: () => { message.success('职级已保存'); onSaved(); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '保存失败'),
  });

  const cols = [
    { title: '职级键', dataIndex: 'gradeKey', width: 100, render: (v: string) => <Tag>{v}</Tag> },
    { title: '显示名', dataIndex: 'gradeKey', render: (k: string) => (
      <Input value={draft[k]?.label ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: { ...d[k], label: e.target.value } }))} style={{ width: 120 }} />
    ) },
    { title: '综合底薪 ¥', dataIndex: 'gradeKey', render: (k: string) => (
      <InputNumber value={draft[k]?.baseSalary} min={0} onChange={(v) => setDraft((d) => ({ ...d, [k]: { ...d[k], baseSalary: Number(v ?? 0) } }))} style={{ width: 120 }} />
    ) },
    { title: '启用', dataIndex: 'gradeKey', width: 80, render: (k: string) => (
      <Switch checked={draft[k]?.isActive} onChange={(v) => setDraft((d) => ({ ...d, [k]: { ...d[k], isActive: v } }))} />
    ) },
    { title: '操作', dataIndex: 'gradeKey', width: 90, render: (k: string) => (
      <Button size="small" type="link" loading={save.isPending} onClick={() => save.mutate(k)}>保存</Button>
    ) },
  ];
  return (
    <Card title="② 职级底薪（综合底薪=基础2500+职级津贴；阶段2 月度自动升降级）">
      <Table rowKey="gradeKey" size="small" loading={loading} pagination={false} dataSource={data?.grades ?? []} columns={cols as any} />
    </Card>
  );
}

// ③ 提成矩阵（职级×班型→单笔奖金；改 = 升版本 + 二次确认）
function MatrixSection({ data, loading, onSaved }: { data?: SalaryConfigData; loading: boolean; onSaved: () => void }) {
  // draft 键 = `${gradeKey}|${classTypeKey}` → 奖金额（允许 undefined 表「未填」，评审#4 用以区分空格 vs 显式 0）
  const [draft, setDraft] = useState<Record<string, number | undefined>>({});
  const grades = data?.grades ?? [];
  const activeClasses = (data?.classTypes ?? []).filter((c) => c.isActive);

  useEffect(() => {
    if (!data?.matrix) return;
    const d: Record<string, number> = {};
    data.matrix.forEach((m) => { d[`${m.gradeKey}|${m.classTypeKey}`] = Number(m.bonusAmount); });
    setDraft(d);
  }, [data?.matrix]);

  const save = useMutation({
    mutationFn: () => {
      const rows: Array<{ gradeKey: string; classTypeKey: string; bonusAmount: number }> = [];
      for (const g of grades) for (const c of activeClasses) {
        rows.push({ gradeKey: g.gradeKey, classTypeKey: c.classTypeKey, bonusAmount: Number(draft[`${g.gradeKey}|${c.classTypeKey}`]) });
      }
      return $.post('/salary/matrix', { rows });
    },
    onSuccess: (r: any) => { message.success(`提成矩阵已升级至 v${r?.version ?? '?'}`); onSaved(); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '保存失败'),
  });

  // 评审#4：保存前校验每个「职级×在职班型」格子已填（非 undefined/NaN），缺则阻断+提示——
  //   杜绝 `?? 0` 把漏填格静默落库成提成=0（启用新班型后该列空、或首版漏填会触发）。
  const missingCells = () => {
    const miss: string[] = [];
    for (const g of grades) for (const c of activeClasses) {
      const v = draft[`${g.gradeKey}|${c.classTypeKey}`];
      if (v === undefined || v === null || Number.isNaN(Number(v))) miss.push(`${g.label}×${c.label}`);
    }
    return miss;
  };
  const handleSave = () => {
    const miss = missingCells();
    if (miss.length) {
      message.error(`有未填格子，请补全后再保存：${miss.slice(0, 4).join('、')}${miss.length > 4 ? ` 等 ${miss.length} 处` : ''}`);
      return;
    }
    save.mutate();
  };

  // 列：第一列职级名 + 每个在职班型一列
  const cols: any[] = [
    { title: '职级 \\ 班型', dataIndex: 'label', width: 110, fixed: 'left', render: (_: any, g: Grade) => <b>{g.label}</b> },
    ...activeClasses.map((c) => ({
      title: `${c.label} ¥${Number(c.standardPrice).toLocaleString()}`,
      dataIndex: c.classTypeKey,
      render: (_: any, g: Grade) => (
        <InputNumber
          value={draft[`${g.gradeKey}|${c.classTypeKey}`]}
          min={0} style={{ width: 100 }}
          status={draft[`${g.gradeKey}|${c.classTypeKey}`] === undefined ? 'error' : undefined}
          onChange={(v) => setDraft((d) => ({ ...d, [`${g.gradeKey}|${c.classTypeKey}`]: (v === null || v === undefined) ? undefined : Number(v) }))}
        />
      ),
    })),
  ];

  return (
    <Card
      title="③ 提成矩阵（单笔奖金/元 · 职级 × 班型）"
      extra={
        <Space>
          <Tag color="blue">当前版本 v{data?.currentVersion ?? 0}</Tag>
          <Popconfirm
            title="确认升级提成矩阵？"
            description={`将保存为新版本（当前 v${data?.currentVersion ?? 0}，提交时自动取 MAX+1）。历史订单按下单时快照锁定、不受影响。`}
            okText="确认升级" cancelText="再想想"
            onConfirm={handleSave}
          >
            <Button type="primary" loading={save.isPending}>保存为新版本（二次确认）</Button>
          </Popconfirm>
        </Space>
      }
    >
      <div style={{ fontSize: 12, color: '#8F959E', marginBottom: 8 }}>
        提成按「下单销售职级 × 班型」取此表绝对奖金额；保存即升版本，订单下单时把版本号快照进订单（重配矩阵不追溯改动历史单）。
      </div>
      <Table rowKey="gradeKey" size="small" loading={loading} pagination={false} dataSource={grades} columns={cols} scroll={{ x: 'max-content' }} />
    </Card>
  );
}
